import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import NavBar from '../components/layout/NavBar'
import { fundEscrow } from '../api/jobs'
import { acceptClientOffer, getClientOffer, type ClientOfferDetail } from '../api/offers'
import { getWalletSummary } from '../api/wallet'

const PIXE_ATOMIC_SCALE = 1_000_000_000_000n

function pixeAtomicToDisplay(value?: number | null) {
  const atomic = BigInt(value || 0)
  const whole = atomic / PIXE_ATOMIC_SCALE
  const frac = atomic % PIXE_ATOMIC_SCALE
  if (frac === 0n) return whole.toString()
  const fracStr = frac.toString().padStart(12, '0').replace(/0+$/, '')
  return `${whole.toString()}.${fracStr}`
}

function pixeStringToAtomic(input: string) {
  const text = String(input || '').trim()
  if (!text) return 0n
  const m = text.match(/^(\d+)(?:\.(\d{1,12}))?$/)
  if (!m) return 0n
  const whole = BigInt(m[1])
  const fracRaw = m[2] || ''
  const frac = BigInt((fracRaw + '0'.repeat(12)).slice(0, 12))
  return whole * PIXE_ATOMIC_SCALE + frac
}

export default function OfferDetailPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [offer, setOffer] = useState<ClientOfferDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [fundedSuccess, setFundedSuccess] = useState(false)
  const [walletBalancePixe, setWalletBalancePixe] = useState<string>('0')
  const [depositPixeAtomic, setDepositPixeAtomic] = useState<number>(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    Promise.all([getClientOffer(id), getWalletSummary({ reconcile: true })])
      .then(([offerData, wallet]) => {
        if (cancelled) return
        setOffer(offerData)
        setWalletBalancePixe(String(wallet?.summary?.pixe_balance || '0'))
        const depAtomic = Number(offerData?.deposit_pixe_atomic || 0)
        setDepositPixeAtomic(Number.isFinite(depAtomic) ? depAtomic : 0)
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'Failed to load offer.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  const walletAtomic = useMemo(() => pixeStringToAtomic(walletBalancePixe), [walletBalancePixe])
  const requiredAtomic = useMemo(() => BigInt(depositPixeAtomic || 0), [depositPixeAtomic])
  const insufficient = useMemo(() => walletAtomic < requiredAtomic, [walletAtomic, requiredAtomic])
  const offerStatus = String(offer?.status || '').toUpperCase()
  const offerAccepted = offerStatus === 'ACCEPTED' || offerStatus === 'FUNDED'

  async function onAccept() {
    if (!offer) return
    setBusy('accept')
    setError(null)
    setNotice(null)
    try {
      const accepted = await acceptClientOffer(offer.offer_id)
      setDepositPixeAtomic(Number(accepted.deposit_pixe_atomic || 0))
      setNotice('Offer accepted. Fund PIXE escrow to start work.')
      const refreshed = await getClientOffer(offer.offer_id)
      setOffer(refreshed)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to accept offer.')
    } finally {
      setBusy(null)
    }
  }

  async function onFundEscrow() {
    if (!offer || !depositPixeAtomic) return
    setBusy('fund')
    setError(null)
    setNotice(null)
    try {
      await fundEscrow(offer.job.id, Number(depositPixeAtomic))
      setNotice('Deposit held in escrow.')
      setFundedSuccess(true)
    } catch (e: any) {
      const data = e?.response?.data || {}
      if (data?.code === 'INSUFFICIENT_AVAILABLE') {
        setError('Insufficient PIXE in wallet.')
      } else {
        setError(data?.detail || e?.message || 'Failed to fund escrow.')
      }
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="min-h-screen">
      <NavBar />
      <main className="mx-auto max-w-4xl space-y-4 px-4 py-6">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-2xl font-bold">Offer</h1>
          <Link to="/offers" className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-sm hover:bg-white/10">Back to offers</Link>
        </div>

        {loading ? <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">Loading offer...</div> : null}
        {error ? <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-200">{error}</div> : null}
        {notice ? <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3 text-sm text-emerald-200">{notice}</div> : null}

        {offer ? (
          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_360px]">
            <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="text-lg font-semibold text-white">{offer.job?.title || 'Offer'}</div>
              <div className="mt-1 text-sm text-white/70">From {offer.pro?.name || offer.pro?.handle || 'Pro'}</div>
              <div className="mt-3 rounded-xl border border-white/10 bg-black/30 p-3 text-sm">
                <div className="text-white/70">Quote amount</div>
                <div className="text-xl font-bold text-white">{offer.amount_pixe || pixeAtomicToDisplay(offer.amount_pixe_atomic)} PIXE</div>
                <div className="mt-1 text-xs text-white/60">{offer.approx_usd ? `~ $${offer.approx_usd}` : 'USD display is approximate only.'}</div>
              </div>
              <div className="mt-3 whitespace-pre-wrap text-sm text-white/85">{offer.description || 'No description provided.'}</div>
            </section>

            <aside className="space-y-3 rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="text-sm font-semibold text-white">Escrow funding (PIXE)</div>
              <div className="text-sm text-white/75">
                Required deposit: {offer.deposit_pixe || pixeAtomicToDisplay(depositPixeAtomic)} PIXE
              </div>
              <div className="text-xs text-white/60">Status: {offerStatus || 'SENT'}</div>
              <div className="text-xs text-white/60">Wallet balance: {walletBalancePixe} PIXE</div>

              {!offerAccepted ? (
                <button
                  type="button"
                  onClick={() => void onAccept()}
                  disabled={Boolean(busy)}
                  className="w-full rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
                >
                  {busy === 'accept' ? 'Preparing...' : 'Accept Offer'}
                </button>
              ) : null}

              {offerAccepted ? (
                fundedSuccess ? (
                  <div className="space-y-2">
                    <div className="rounded-lg border border-emerald-300/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100">
                      Deposit held in escrow.
                    </div>
                    <div className="text-xs text-white/65">You can cancel/refund until work begins.</div>
                    <Link
                      to={`/jobs/${offer.job.id}`}
                      className="block w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-center text-sm font-semibold text-white hover:bg-white/10"
                    >
                      View Job
                    </Link>
                  </div>
                ) : insufficient ? (
                  <div className="space-y-2">
                    <div className="rounded-lg border border-amber-300/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
                      Insufficient PIXE balance to fund escrow.
                    </div>
                    <Link
                      to="/settings/wallet"
                      className="block w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-center text-sm font-semibold text-white hover:bg-white/10"
                    >
                      Buy PIXE
                    </Link>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => void onFundEscrow()}
                    disabled={Boolean(busy)}
                    className="w-full rounded-lg bg-emerald-500 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-60"
                  >
                    {busy === 'fund' ? 'Funding...' : `Pay ${offer.deposit_pixe || pixeAtomicToDisplay(depositPixeAtomic)} PIXE (held in escrow)`}
                  </button>
                )
              ) : null}
              {offerStatus === 'FUNDED' && !fundedSuccess ? (
                <Link to={`/jobs/${offer.job.id}`} className="block w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-center text-sm font-semibold text-white hover:bg-white/10">
                  Open Funded Job
                </Link>
              ) : null}
            </aside>
          </div>
        ) : null}
      </main>
    </div>
  )
}
